import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import {
  CopySimpleIcon,
  DotsThreeIcon,
  PencilSimpleIcon,
  PlusIcon,
  ShieldCheckIcon,
} from '@phosphor-icons/react';
import { Action, Subject } from '@erp/authz';
import { ValidationRuleStatus } from '@erp/shared-types';
import type {
  ValidationRuleDTO,
  ValidationRuleLastRun,
  ValidationRuleListItemDTO,
} from '@erp/rpc';
import PaginatedGentlyTable, {
  type PaginationConfig,
  type TableColumn,
} from '@/components/common/PaginatedGentlyTable';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Can } from '@/hooks/useCan';
import { useUsersInOrg } from '@/api/rpc/hooks/core/useUsersInOrg';
import {
  useDuplicateValidationRule,
  useValidationRules,
} from '@/api/rpc/hooks/inventory-management/useValidationRules';
import { cn } from '@/lib/utils';
import Page from 'src/components/page/Page';
import {
  RuleStatusBadge,
  RunStatusIcon,
  formatValidationTimestamp,
  useRuleTypeLabels,
} from './components/validationRuleDisplay';
import { ValidationRuleWizardDialog } from './wizard/ValidationRuleWizard';

/** The rules list's own URL — a deeper pathname means a child route is active. */
export const VALIDATIONS_PATH = '/item-master/validations';

const DEFAULT_PAGE_SIZE = 25;

/** "Every status", as the filter's own key — `status` is simply omitted then. */
const ALL_STATUSES = 'all';

type StatusFilterValue = ValidationRuleStatus | typeof ALL_STATUSES;

// ---------------------------------------------------------------------------
// Status filter
// ---------------------------------------------------------------------------

interface StatusFilterProps {
  value: StatusFilterValue;
  onChange: (value: StatusFilterValue) => void;
}

function StatusFilter({ value, onChange }: StatusFilterProps) {
  const gt = useGT();

  const options: { key: StatusFilterValue; label: string }[] = [
    {
      key: ALL_STATUSES,
      label: gt('All', { context: 'validation rule status filter, no filter' }),
    },
    {
      key: ValidationRuleStatus.Draft,
      label: gt('Draft', { context: 'validation rule status filter' }),
    },
    {
      key: ValidationRuleStatus.Active,
      label: gt('Active', { context: 'validation rule status filter' }),
    },
    {
      key: ValidationRuleStatus.Paused,
      label: gt('Paused', { context: 'validation rule status filter' }),
    },
  ];

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide whitespace-nowrap">
        <T context="label for the validation rule status filter">Status</T>
      </span>
      <div className="flex rounded-md border overflow-hidden text-xs">
        {options.map((option, index) => (
          <button
            key={option.key}
            type="button"
            onClick={() => onChange(option.key)}
            aria-pressed={option.key === value}
            className={cn(
              'px-3 py-1',
              index > 0 && 'border-l',
              option.key === value
                ? 'bg-primary text-primary-foreground'
                : 'bg-background text-foreground hover:bg-muted',
            )}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cells
// ---------------------------------------------------------------------------

/** When the rule last ran and how that run ended. */
function LastRunCell({ lastRun }: { lastRun: ValidationRuleLastRun | null }) {
  if (!lastRun) {
    return (
      <span className="text-sm text-muted-foreground italic">
        <T context="validation rules table cell, rule has never run">
          Never run
        </T>
      </span>
    );
  }

  return (
    <span className="flex items-center gap-1.5 text-sm whitespace-nowrap">
      <RunStatusIcon status={lastRun.status} />
      {formatValidationTimestamp(lastRun.time)}
    </span>
  );
}

/** Read-only assignee name, resolved against the org's users. */
function AssigneeCell({ userId }: { userId: string | null }) {
  const { data: users } = useUsersInOrg();
  if (!userId) return <span className="text-sm text-muted-foreground">—</span>;
  const user = users?.find((candidate) => candidate.id === userId);
  return <span className="text-sm">{user?.name ?? '—'}</span>;
}

/** A count column reads as "nothing to do" when it is zero. */
function CountCell({ count }: { count: number }) {
  return (
    <span className={cn('text-sm', count === 0 && 'text-muted-foreground')}>
      {count}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Rule builder entry points
// ---------------------------------------------------------------------------

/** Opens the rule builder on a blank rule. */
function NewRuleButton({ onClick }: { onClick: () => void }) {
  return (
    <Can I={Action.Update} a={Subject.Product}>
      <Button size="sm" onClick={onClick}>
        <PlusIcon className="h-4 w-4" />
        <T context="button that starts authoring a new validation rule">
          New Rule
        </T>
      </Button>
    </Can>
  );
}

/**
 * Per-row Edit and Duplicate. Duplicating goes through the `duplicate`
 * procedure — the copy is a draft named "Copy of …" server-side — and then
 * opens the builder on that copy, so the author edits a real rule rather than a
 * form pre-filled with someone else's.
 */
function RuleActionsCell({
  rule,
  onEdit,
  onDuplicate,
  duplicating,
}: {
  rule: ValidationRuleListItemDTO;
  onEdit: (rule: ValidationRuleListItemDTO) => void;
  onDuplicate: (rule: ValidationRuleListItemDTO) => void;
  duplicating: boolean;
}) {
  const gt = useGT();

  return (
    <Can I={Action.Update} a={Subject.Product}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            aria-label={gt('Rule actions', {
              context: 'aria label of the per-row validation rule menu',
            })}
            // The row itself opens the rule; the menu must not.
            onClick={(event) => event.stopPropagation()}
          >
            <DotsThreeIcon className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => onEdit(rule)}>
            <PencilSimpleIcon className="h-4 w-4" />
            <T context="row action that opens a validation rule in the builder">
              Edit
            </T>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => onDuplicate(rule)}
            disabled={duplicating}
          >
            <CopySimpleIcon className="h-4 w-4" />
            <T context="row action that copies a validation rule">Duplicate</T>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </Can>
  );
}

// ---------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------

interface RuleRowActions {
  onEdit: (rule: ValidationRuleListItemDTO) => void;
  onDuplicate: (rule: ValidationRuleListItemDTO) => void;
  duplicating: boolean;
}

function useRuleColumns(
  actions: RuleRowActions,
): TableColumn<ValidationRuleListItemDTO>[] {
  const gt = useGT();
  const ruleTypeLabels = useRuleTypeLabels();

  return [
    {
      id: 'name',
      label: gt('Rule', {
        context: 'validation rules table column, the rule name',
      }),
      className: 'py-3',
      render: (_value: unknown, row) => (
        <span className="text-sm font-medium">{row.name}</span>
      ),
    },
    {
      id: 'schemaName',
      label: gt('Table', {
        context: 'validation rules table column, the AS/400 schema validated',
      }),
      className: 'py-3',
      render: (_value: unknown, row) => (
        <span className="text-sm font-mono text-muted-foreground">
          {row.schemaName}
        </span>
      ),
    },
    {
      id: 'ruleType',
      label: gt('Type', {
        context: 'validation rules table column, the kind of rule',
      }),
      className: 'py-3',
      render: (_value: unknown, row) => (
        <span className="text-sm">{ruleTypeLabels[row.ruleType]}</span>
      ),
    },
    {
      id: 'status',
      label: gt('Status', {
        context: 'validation rules table column, the rule lifecycle status',
      }),
      className: 'py-3',
      render: (_value: unknown, row) => <RuleStatusBadge status={row.status} />,
    },
    {
      id: 'lastRun',
      label: gt('Last evaluated', {
        context: 'validation rules table column, when the rule last ran',
      }),
      className: 'py-3',
      render: (_value: unknown, row) => <LastRunCell lastRun={row.lastRun} />,
    },
    {
      id: 'openViolationCount',
      label: gt('Open violations', {
        context: 'validation rules table column, unresolved violation count',
      }),
      align: 'right',
      className: 'py-3',
      render: (_value: unknown, row) => (
        <CountCell count={row.openViolationCount} />
      ),
    },
    {
      id: 'openReviewCount',
      label: gt('Open reviews', {
        context:
          'validation rules table column, draft reviews staged from this rule',
      }),
      align: 'right',
      className: 'py-3',
      render: (_value: unknown, row) => (
        <CountCell count={row.openReviewCount} />
      ),
    },
    {
      id: 'assigneeUserId',
      label: gt('Assignee', {
        context: 'validation rules table column, who owns the rule',
      }),
      className: 'py-3',
      render: (_value: unknown, row) => (
        <AssigneeCell userId={row.assigneeUserId} />
      ),
    },
    {
      // `id` addresses a row field; this column renders actions rather than one,
      // so it borrows the row's own id and never sorts by it.
      id: 'id',
      label: '',
      align: 'right',
      className: 'py-3 w-10',
      stopRowClick: true,
      render: (_value: unknown, row) => (
        <RuleActionsCell
          rule={row}
          onEdit={actions.onEdit}
          onDuplicate={actions.onDuplicate}
          duplicating={actions.duplicating}
        />
      ),
    },
  ];
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

/**
 * The org's validation rules: what each one checks, whether it is live, and how
 * much it currently flags. Rows open the rule's detail page.
 */
export function ValidationsPage() {
  const gt = useGT();
  const navigate = useNavigate();
  const location = useLocation();

  const [status, setStatus] = useState<StatusFilterValue>(ALL_STATUSES);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Null with the dialog open means "author a new rule"; a rule means "edit it".
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardRule, setWizardRule] = useState<ValidationRuleDTO | null>(null);
  const duplicateRule = useDuplicateValidationRule();

  function openWizard(rule: ValidationRuleDTO | null) {
    setWizardRule(rule);
    setWizardOpen(true);
  }

  async function handleDuplicate(rule: ValidationRuleListItemDTO) {
    try {
      const result = await duplicateRule.mutateAsync({ id: rule.id });
      openWizard(result.rule);
    } catch {
      // The mutation's own toast reports the failure — there is no copy to open.
    }
  }

  const columns = useRuleColumns({
    onEdit: openWizard,
    onDuplicate: (rule) => void handleDuplicate(rule),
    duplicating: duplicateRule.isPending,
  });

  // When on a child route (a rule's detail page), render the Outlet only.
  const isChildRoute = location.pathname !== VALIDATIONS_PATH;

  const { rules, total, isLoading, error } = useValidationRules(
    {
      status: status === ALL_STATUSES ? undefined : status,
      page,
      pageSize,
    },
    !isChildRoute,
  );

  const paginationConfig: PaginationConfig = {
    enabled: total > pageSize,
    pagination: {
      pageSize,
      currentPage: page,
      hasNextPage: page * pageSize < total,
      hasPreviousPage: page > 1,
      totalItems: total,
      startItem: total === 0 ? 0 : (page - 1) * pageSize + 1,
      endItem: Math.min(page * pageSize, total),
    },
    onPageSizeChange: (size) => {
      setPageSize(size);
      setPage(1);
    },
    onPreviousPage: () => setPage((current) => Math.max(1, current - 1)),
    onNextPage: () => setPage((current) => current + 1),
    onFirstPage: () => setPage(1),
    onLastPage: () => setPage(Math.ceil(total / pageSize) || 1),
    onGoToPage: setPage,
    showRowsPerPage: true,
    showPageInfo: true,
    showPageNumbers: true,
  };

  if (isChildRoute) {
    return <Outlet />;
  }

  return (
    <Page
      title={<T context="page title, item master validations">Validations</T>}
      subtitle={
        <T context="page subtitle explaining what validation rules do">
          Rules that flag where divisions disagree on the same item.
        </T>
      }
      actions={<NewRuleButton onClick={() => openWizard(null)} />}
      isLoading={isLoading && rules.length === 0}
      error={error ?? undefined}
    >
      <div className="space-y-2">
        <StatusFilter
          value={status}
          onChange={(next) => {
            setStatus(next);
            setPage(1);
          }}
        />

        {/* With no rules at all, only the dedicated empty state below renders —
            showing the table would put its own "No validation rules" message
            right above it. The table's message stays for the filtered case. */}
        {!(rules.length === 0 && !isLoading && status === ALL_STATUSES) && (
          <PaginatedGentlyTable<ValidationRuleListItemDTO>
            data={rules}
            columns={columns}
            onRowClick={(rule) =>
              void navigate({
                to: '/item-master/validations/$ruleId',
                params: { ruleId: rule.id },
              })
            }
            emptyMessage={gt('No validation rules', {
              context: 'empty state in the validation rules table',
            })}
            showSearch={false}
            showDownload={false}
            isLoading={isLoading}
            paginationConfig={paginationConfig}
            getRowKey={(row) => row.id}
          />
        )}

        {rules.length === 0 && !isLoading && status === ALL_STATUSES && (
          <div className="flex flex-col items-center gap-2 py-16 text-muted-foreground">
            <ShieldCheckIcon className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium">
              <T context="empty state heading, no validation rules yet">
                No validation rules yet
              </T>
            </p>
            <p className="text-xs">
              <T context="empty state subtext, no validation rules yet">
                A rule groups catalog rows and flags the groups whose fields
                disagree.
              </T>
            </p>
            <NewRuleButton onClick={() => openWizard(null)} />
          </div>
        )}
      </div>

      <ValidationRuleWizardDialog
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        rule={wizardRule}
      />
    </Page>
  );
}
